import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { color, radius, space } from "@tokens/index";
import { Surface, Text, useViewport } from "@primitives/index";

/**
 * Rows for admin lists, per charter §3 and §7.
 *
 * `DataRow` is a flat card: title line, one meta line, trailing controls.
 * The controls stay on the title line on every class and align to its top;
 * on `compact` the caller collapses them to a kebab so the title keeps its
 * width. Every text cell has `minWidth: 0` and truncates.
 *
 * `SelectableRow` is a row that selects: `bg.selected` plus a 3px accent
 * bar on the leading edge when selected; `bg.hover` on hover.
 */

interface DataRowProps {
    readonly title: ReactNode;
    readonly meta?: ReactNode;
    /** Badges that sit on the title line. */
    readonly badges?: ReactNode;
    readonly leading?: ReactNode;
    readonly trailing?: ReactNode;
    readonly children?: ReactNode;
    readonly disabled?: boolean;
    readonly testId?: string;
}

export function DataRow({
    title,
    meta,
    badges,
    leading,
    trailing,
    children,
    disabled = false,
    testId,
}: DataRowProps): ReactElement {
    const { isCompact } = useViewport();
    return (
        <Surface
            elevation={1}
            borderRadius="lg"
            padding={isCompact ? 3 : 4}
            data-testid={testId}
            style={{ width: "100%", minWidth: 0, opacity: disabled ? 0.56 : 1 }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: isCompact ? "flex-start" : "center",
                    gap: space[3],
                    minWidth: 0,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: space[3],
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    {leading}
                    <div style={{ display: "grid", gap: space[1], minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: space[2],
                                minWidth: 0,
                            }}
                        >
                            {typeof title === "string" ? (
                                <Text role="body" weight="medium" truncate>
                                    {title}
                                </Text>
                            ) : (
                                title
                            )}
                            {badges}
                        </div>
                        {meta !== undefined &&
                            (typeof meta === "string" ? (
                                <Text role="caption" tone="muted" truncate>
                                    {meta}
                                </Text>
                            ) : (
                                meta
                            ))}
                    </div>
                </div>
                {trailing !== undefined && (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: space[2],
                            flexShrink: 0,
                            alignSelf: isCompact ? "flex-start" : "center",
                        }}
                    >
                        {trailing}
                    </div>
                )}
            </div>
            {children !== undefined && (
                <div style={{ marginTop: space[3], display: "grid", gap: space[3], minWidth: 0 }}>
                    {children}
                </div>
            )}
        </Surface>
    );
}

interface SelectableRowProps {
    readonly selected: boolean;
    readonly onSelect: () => void;
    readonly title: string;
    readonly meta?: string;
    readonly badges?: ReactNode;
    readonly testId?: string;
}

export function SelectableRow({
    selected,
    onSelect,
    title,
    meta,
    badges,
    testId,
}: SelectableRowProps): ReactElement {
    const [hovered, setHovered] = useState(false);

    const style: CSSProperties = {
        position: "relative",
        display: "grid",
        gap: space[1],
        width: "100%",
        minHeight: 56,
        padding: `${space[3]} ${space[4]}`,
        borderRadius: radius.lg,
        border: `1px solid ${color.border.subtle}`,
        background: selected ? color.bg.selected : hovered ? color.bg.hover : color.bg.surface,
        color: color.fg.default,
        textAlign: "left",
        cursor: "pointer",
        overflow: "hidden",
        appearance: "none",
    };

    return (
        <button
            type="button"
            aria-current={selected ? "true" : undefined}
            data-selected={selected || undefined}
            data-testid={testId}
            style={style}
            onClick={onSelect}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {selected && (
                <span
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        insetBlock: 0,
                        left: 0,
                        width: 3,
                        background: color.accent.primary,
                    }}
                />
            )}
            <div style={{ display: "flex", alignItems: "center", gap: space[2], minWidth: 0 }}>
                <Text role="body" weight="medium" truncate>
                    {title}
                </Text>
                {badges}
            </div>
            {meta !== undefined && (
                <Text role="caption" tone="muted" truncate>
                    {meta}
                </Text>
            )}
        </button>
    );
}
